import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { StructureSet } from '~/domain/patient/structureset';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { CopyStructureSetResult } from '~/domain/services/structureSetService/structureSetCopyService';
import { useLiveStructureSet } from '~/hooks/domain/useLiveStructureSet';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { CopyTarget } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/ContouringStep/StructureSet/CopyStructureSetDialog/types';
import { localization } from '~/i18n';
import { enqueueSnackbar } from '~/utils/notifications';
import { redactDicomUids } from '~/utils/phiRedaction';
import { useCopyStructureSet } from './useCopyStructureSet';

/**
 * Joins copy caveats into one sentence: "a", "a and b", "a, b and c".
 */
function formatCaveats(caveats: readonly string[]): string {
    if (caveats.length === 1) {
        return caveats[0];
    }
    return `${caveats.slice(0, -1).join(', ')} and ${caveats[caveats.length - 1]}`;
}

/**
 * Single source of truth for the copy-structures flow, shared by every entry
 * point (structure-set header, single-ROI menu, bulk panel). It resolves the
 * domain patient / source structure set / source image for the given read
 * model and orchestrates confirm: the domain service picks the copy variant
 * and reports skipped / empty-volume ROIs, while this hook loads the result
 * into the viewport and surfaces count-only (PHI-free) messaging.
 *
 * Domain resolution goes through the documented `useLiveStructureSet` escape
 * hatch, so no caller has to reach into structure-set internals itself.
 *
 * `onSuccess` runs once the copy has committed (before the viewport reload),
 * so each entry point can close its own dialog at that point.
 */
export function useCopyStructureSetFlow(
    sourceStructureSet: StructureSetReadModel | null,
    onSuccess: () => void
): {
    patient: Patient | null;
    domainStructureSet: StructureSet | null;
    sourceImage: Image3D | null;
    handleConfirmCopy: (target: CopyTarget) => Promise<void>;
} {
    const env = useActiveEditTarget().environment;
    const patientRepository = useRecoilValue(patientRepositoryState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const { copyToTarget } = useCopyStructureSet();
    const { setEnvironmentStructureSet } = useViewportEnvironment();

    const patient = useMemo(
        () =>
            selectedPatientId
                ? (patientRepository.getByPatientId(selectedPatientId) ?? null)
                : null,
        [patientRepository, selectedPatientId]
    );

    const domainStructureSet = useLiveStructureSet(sourceStructureSet?.id);
    const sourceImage = domainStructureSet?.image ?? null;

    const handleConfirmCopy = useCallback(
        async (target: CopyTarget): Promise<void> => {
            if (!domainStructureSet || !patient) {
                return;
            }
            if (!env) {
                // The dialog can open before the engine environment is ready;
                // surface it rather than dropping the copy silently.
                enqueueSnackbar(
                    'Cannot copy structures yet — the viewer is still loading.',
                    { variant: 'error' }
                );
                return;
            }

            let result: CopyStructureSetResult | null;
            try {
                result = await copyToTarget(
                    {
                        sourceStructureSet: domainStructureSet,
                        targetImage: target.image,
                        selectedRoiNumbers: target.selectedRoiNumbers,
                        existingStructureSets: patient.structureSets,
                        targetStructureSet: target.targetStructureSet,
                        registration: target.registration,
                        sameFrameOfReference: target.sameFrameOfReference,
                        overwriteExisting: target.overwriteExisting,
                    },
                    patient
                );
            } catch (error) {
                // The copy failed before committing anything. Copy errors can
                // embed DICOM UIDs (frame of reference, SOP instance); redact
                // before logging and show a fixed, PHI-free message.
                console.error(
                    'Failed to copy structures:',
                    redactDicomUids(error)
                );
                enqueueSnackbar(
                    'Failed to copy structures. Please try again.',
                    {
                        variant: 'error',
                    }
                );
                return;
            }

            if (!result) {
                // Refused before copying; copyToTarget told the user why.
                return;
            }

            // The copy is committed once copyToTarget returns. From here a
            // failure is only a viewport-refresh problem, never a copy failure,
            // so it must not be reported as "failed to copy".
            onSuccess();

            try {
                setEnvironmentStructureSet(
                    [result.structureSet],
                    patient.dataSet.PatientID
                );
            } catch (error) {
                console.error(
                    'Copied structures but failed to load them into the viewport:',
                    redactDicomUids(error)
                );
                enqueueSnackbar(
                    'Structures copied, but the viewer could not be refreshed. ' +
                        'Reload to see them.',
                    { variant: 'warning' }
                );
                return;
            }

            const skippedCount = result.skippedRois.length;
            const emptyCount = result.emptyVolumeRoiNumbers.length;
            const namingFailureCount = result.namingFailures.length;
            const demotedCount = result.externalDemotions.length;

            const caveats: string[] = [];
            if (skippedCount > 0) {
                caveats.push(`${skippedCount} structure(s) were skipped`);
            }
            if (emptyCount > 0) {
                caveats.push(`${emptyCount} structure(s) have empty volumes`);
            }
            if (namingFailureCount > 0) {
                caveats.push(
                    `${namingFailureCount} structure(s) kept a temporary name and must be renamed`
                );
            }
            // A copy that quietly stops being the body contour changes what
            // export and dose calculation treat as the patient outline, so the
            // demotion the domain reports is surfaced, not absorbed. Count
            // only — structure names stay out of the message, as above.
            if (demotedCount > 0) {
                caveats.push(
                    localization.t('structure.copy.externalDemoted', {
                        count: demotedCount,
                    })
                );
            }

            if (caveats.length === 0) {
                enqueueSnackbar('Structures copied successfully.', {
                    variant: 'success',
                });
                return;
            }

            enqueueSnackbar(
                `Structures copied successfully. ${formatCaveats(caveats)}.`,
                { variant: 'warning' }
            );
        },
        [
            domainStructureSet,
            patient,
            env,
            copyToTarget,
            setEnvironmentStructureSet,
            onSuccess,
        ]
    );

    return { patient, domainStructureSet, sourceImage, handleConfirmCopy };
}
